import { of } from 'rxjs'
import {
  filter,
  map,
  withLatestFrom,
  catchError,
  switchMap,
  tap,
  mergeMap
} from 'rxjs/operators'
import Actions from 'actions'
import { getPayload, catchAndDispatchError } from 'utils/rxUtils'
import { isRootRoute, getParameterFromURL } from 'utils/routingUtils'
import loginService from 'services/loginService'
import apis from '../apis'
import { LAYOUT_TYPE, relationship } from 'consts'
import { brokerDeserializer } from 'utils/serializers/brokerDeserializer'
import { getPlanFileUrl } from 'utils/planUtils'

export const brokerWelcomeBack = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APP_LAUNCHED),
    withLatestFrom(state$),
    map(([_, state]) => state),
    filter(state => isRootRoute(state)),
    map(state => getParameterFromURL(state, 'token')),
    filter(exists => exists),
    mergeMap(token => {
      return [
        Actions.createAction(Actions.BROKER_WELCOME_BACK_REQUESTED, { token }),
        Actions.createAction(Actions.LAYOUT_APPLIED, {
          layout: LAYOUT_TYPE.broker
        })
      ]
    })
  )

export const removeTokenOnSelfLayout = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APP_LAUNCHED),
    withLatestFrom(state$),
    map(([_, state]) => state),
    filter(isRootRoute),
    filter(state => !getParameterFromURL(state, 'token')),
    filter(
      state => getParameterFromURL(state, 'layout') !== LAYOUT_TYPE.broker
    ),
    tap(() => loginService.removeToken()),
    map(() =>
      Actions.createAction(Actions.LAYOUT_APPLIED, {
        layout: LAYOUT_TYPE.self_enroll
      })
    )
  )

export const brokerWelcomeBackLayout = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APP_LAUNCHED),
    withLatestFrom(state$),
    map(([_, state]) => state),
    filter(
      state => getParameterFromURL(state, 'layout') === LAYOUT_TYPE.broker
    ),
    map(() => {
      const token = loginService.getToken()
      return Actions.createAction(Actions.BROKER_WELCOME_BACK_REQUESTED, {
        token
      })
    })
  )

export const validateBrokerToken = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.BROKER_WELCOME_BACK_REQUESTED),
    tap(({ token }) => loginService.setToken(token)),
    filter(({ token }) => token),
    switchMap(token =>
      apis.validateBrokerToken(token).pipe(
        map(brokerDeserializer),
        withLatestFrom(state$),
        mergeMap(([payload, state]) => {
          const applicationId = getParameterFromURL(state, 'application_id')
          return [
            Actions.createAction(Actions.BROKER_WELCOME_BACK_RECEIVED, {
              applicationId
            }),
            Actions.createAction(Actions.BROKER_DATA_UPDATED, payload)
          ]
        }),
        catchError(error =>
          of(Actions.createAction(Actions.BROKER_TIMEOUT_MODAL_OPENED, error))
        )
      )
    )
  )

export const resumeBrokerApplication = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.BROKER_WELCOME_BACK_RECEIVED),
    withLatestFrom(state$),
    filter(([payload, state]) => {
      const hasApplicationId = Boolean(payload.applicationId)
      const stateHasApplicationId = Boolean(
        state.application && state.application.applicationId
      )
      return hasApplicationId && !stateHasApplicationId
    }),
    map(([payload]) =>
      Actions.createAction(Actions.APPLICATION_DATA_REQUESTED, {
        applicationId: payload.applicationId
      })
    )
  )

export const shareQuote = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SHARE_QUOTE_REQUESTED),
    withLatestFrom(state$),
    map(([{ email, linkToApplications }, state]) => {
      const owner = state.member.members.find(
        member => member.relation === relationship.owner
      )
      const enrolee_name =
        owner?.firstName && owner?.lastName
          ? `${owner.firstName} ${owner.lastName}`
          : null

      let plansToSend = state.plan.comparePlans
      if (!plansToSend?.length) {
        plansToSend = [state.plan.selectedPlan]
      }
      plansToSend = plansToSend.map(plan => {
        return {
          ...plan,
          plan_file_url: getPlanFileUrl(plan)
        }
      })
      return {
        email,
        image: state.app.imageToShare,
        plans: plansToSend,
        enrolee_name: enrolee_name,
        link_to_application: linkToApplications,
        application_id: state.application.applicationId
      }
    }),
    switchMap(data =>
      apis.shareQuote(data).pipe(
        map(() => Actions.createAction(Actions.SHARE_QUOTE_RECEIVED)),
        catchAndDispatchError
      )
    )
  )

export const applyLayoutByViewer = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.APPLICATION_DATA_RECEIVED),
    map(({ data }) => data),
    map(({ viewer_type }) => {
      if (viewer_type == 'broker') {
        return LAYOUT_TYPE.broker
      } else {
        return LAYOUT_TYPE.self_enroll
      }
    }),
    map(layout =>
      Actions.createAction(Actions.LAYOUT_APPLIED, {
        layout
      })
    )
  )